import { useState, useEffect, useCallback } from "react";
import { api } from "../api";
import { today, formatDate, formatMonth, pct, calcAccuracy, accuracyColor, diffInfo, comma } from "../constants";
import Card from "./ui/Card";
import Input from "./ui/Input";
import Select from "./ui/Select";
import Btn from "./ui/Btn";
import LoadingSpinner from "./ui/LoadingSpinner";
import EmptyState from "./ui/EmptyState";

export default function ReviewerApp({ user, stations, toast, onLogout }) {
  const [tab, setTab] = useState("entry");
  const [form, setForm] = useState({
    date: today(),
    station_id: "",
    camera_replies: "",
    notes: "",
  });
  const [saved, setSaved] = useState([]);
  const [loading, setLoading] = useState(false);
  const [attForm, setAttForm] = useState({
    date: today(),
    station_id: "",
    check_in: "",
    check_out: "",
    notes: "",
  });
  const [attRecords, setAttRecords] = useState([]);
  const [selMonth, setSelMonth] = useState(today().slice(0, 7));

  const loadSaved = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api(`/reviewer/summary?month=${selMonth}`);
      setSaved(r || []);
    } catch {
      /* ignore */
    }
    setLoading(false);
  }, [selMonth]);

  const loadAtt = useCallback(async () => {
    try {
      const r = await api(
        `/attendance?date_from=${attForm.date}&date_to=${attForm.date}`
      );
      setAttRecords(r || []);
    } catch {
      /* ignore */
    }
  }, [attForm.date]);

  useEffect(() => {
    if (tab === "history") loadSaved();
  }, [tab, loadSaved]);

  useEffect(() => {
    if (tab === "attendance") loadAtt();
  }, [tab, loadAtt]);

  const submit = async () => {
    if (!form.station_id || form.camera_replies === "") {
      toast("يرجى ملء الحقول المطلوبة", "warning");
      return;
    }
    try {
      await api("/entries", {
        method: "POST",
        body: {
          date: form.date,
          station_id: parseInt(form.station_id),
          camera_replies: parseInt(form.camera_replies || 0),
          notes: form.notes || null,
        },
      });
      toast("✅ تم حفظ السجل");
      setForm((f) => ({
        ...f,
        station_id: "",
        camera_replies: "",
        notes: "",
      }));
    } catch (e) {
      toast("خطأ: " + e.message, "error");
    }
  };

  const submitAtt = async () => {
    if (!attForm.station_id) {
      toast("اختر المحطة", "warning");
      return;
    }
    if (!attForm.check_in && !attForm.check_out) {
      toast("أدخل وقت الحضور أو الانصراف", "warning");
      return;
    }
    try {
      await api("/attendance", {
        method: "POST",
        body: {
          date: attForm.date,
          station_id: parseInt(attForm.station_id),
          check_in: attForm.check_in || null,
          check_out: attForm.check_out || null,
          notes: attForm.notes || null,
        },
      });
      toast("✅ تم تسجيل الدوام");
      setAttForm((f) => ({
        ...f,
        station_id: "",
        check_in: "",
        check_out: "",
        notes: "",
      }));
      loadAtt();
    } catch (e) {
      toast("خطأ: " + e.message, "error");
    }
  };

  return (
    <div
      dir="rtl"
      style={{
        minHeight: "100vh",
        background: "#020817",
        fontFamily: "'Cairo','Tajawal',sans-serif",
        maxWidth: 480,
        margin: "0 auto",
      }}
    >
      {/* Header */}
      <div
        style={{
          background: "linear-gradient(135deg,#0f172a,#0f2744)",
          borderBottom: "1px solid #1e3a5f",
          padding: "14px 20px",
          position: "sticky",
          top: 0,
          zIndex: 100,
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <div>
            <div style={{ color: "#f1f5f9", fontWeight: 800, fontSize: 15 }}>
              📷 مراجعة الكاميرات
            </div>
            <div style={{ color: "#38bdf8", fontSize: 12, marginTop: 1 }}>
              👤 {user.full_name}
            </div>
          </div>
          <button
            onClick={onLogout}
            style={{
              background: "#1e293b",
              border: "1px solid #334155",
              borderRadius: 8,
              color: "#94a3b8",
              padding: "7px 13px",
              cursor: "pointer",
              fontSize: 12,
              fontFamily: "inherit",
            }}
          >
            خروج
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div
        style={{
          display: "flex",
          background: "#0f172a",
          borderBottom: "1px solid #1e293b",
        }}
      >
        {[
          ["entry", "📝", "إدخال"],
          ["history", "📊", "سجلاتي"],
          ["stations", "🏭", "محطاتي"],
          ["attendance", "🕐", "الدوام"],
        ].map(([id, icon, lbl]) => (
          <button
            key={id}
            onClick={() => setTab(id)}
            style={{
              flex: 1,
              background:
                tab === id
                  ? "linear-gradient(135deg,#38bdf8,#818cf8)"
                  : "transparent",
              color: tab === id ? "#fff" : "#64748b",
              border: "none",
              padding: "13px 4px",
              fontSize: 12,
              fontFamily: "inherit",
              fontWeight: tab === id ? 700 : 400,
              cursor: "pointer",
            }}
          >
            <div style={{ fontSize: 18 }}>{icon}</div>
            <div>{lbl}</div>
          </button>
        ))}
      </div>

      <div style={{ padding: "16px 16px 100px" }}>
        {/* ENTRY */}
        {tab === "entry" && (
          <Card>
            <h3
              style={{
                color: "#f1f5f9",
                margin: "0 0 16px",
                fontSize: 15,
                fontWeight: 700,
              }}
            >
              إدخال ردود الكاميرات
            </h3>
            <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
              <Input
                label="التاريخ"
                type="date"
                value={form.date}
                onChange={(v) => setForm((f) => ({ ...f, date: v }))}
              />
              <Select
                label="المحطة"
                value={form.station_id}
                onChange={(v) => setForm((f) => ({ ...f, station_id: v }))}
                options={stations.map((s) => ({
                  value: s.id,
                  label: s.name + " — " + s.region,
                }))}
                required
              />
              <Input
                label="عدد ردود الكاميرات"
                type="number"
                value={form.camera_replies}
                onChange={(v) => setForm((f) => ({ ...f, camera_replies: v }))}
                required
              />
              <Input
                label="ملاحظات"
                value={form.notes}
                onChange={(v) => setForm((f) => ({ ...f, notes: v }))}
                placeholder="اختياري..."
              />
              <button
                onClick={submit}
                style={{
                  background: "linear-gradient(135deg,#22c55e,#16a34a)",
                  color: "#fff",
                  border: "none",
                  borderRadius: 10,
                  padding: 16,
                  fontSize: 16,
                  fontFamily: "inherit",
                  fontWeight: 700,
                  cursor: "pointer",
                  marginTop: 4,
                }}
              >
                💾 حفظ
              </button>
            </div>
          </Card>
        )}

        {/* HISTORY */}
        {tab === "history" && (
          <div>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: 14,
              }}
            >
              <h3
                style={{
                  color: "#f1f5f9",
                  margin: 0,
                  fontSize: 15,
                  fontWeight: 700,
                }}
              >
                سجلاتي — {formatMonth(selMonth)}
              </h3>
              <input
                type="month"
                value={selMonth}
                onChange={(e) => setSelMonth(e.target.value)}
                style={{
                  background: "#1e293b",
                  border: "1px solid #334155",
                  borderRadius: 8,
                  padding: "7px 10px",
                  color: "#f1f5f9",
                  fontSize: 12,
                  fontFamily: "inherit",
                }}
              />
            </div>
            {loading ? (
              <LoadingSpinner />
            ) : saved.length === 0 ? (
              <EmptyState message="لا توجد سجلات" />
            ) : (
              saved.map((e, i) => {
                const di = diffInfo(e.diff);
                const acc = calcAccuracy(e.camera_replies, e.operational_replies);
                return (
                  <Card
                    key={i}
                    style={{ padding: "14px 16px", marginBottom: 10 }}
                  >
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-start",
                        marginBottom: 10,
                      }}
                    >
                      <div>
                        <div
                          style={{
                            color: "#f1f5f9",
                            fontWeight: 700,
                            fontSize: 14,
                          }}
                        >
                          {e.station_name}
                        </div>
                        <div
                          style={{
                            color: "#64748b",
                            fontSize: 11,
                            marginTop: 2,
                          }}
                        >
                          {formatDate(e.entry_date)}
                        </div>
                      </div>
                      <span
                        style={{
                          background: `${di.color}22`,
                          color: di.color,
                          borderRadius: 8,
                          padding: "4px 10px",
                          fontSize: 12,
                          fontWeight: 800,
                          border: `1px solid ${di.color}44`,
                          textAlign: "center",
                        }}
                      >
                        {comma(di.magnitude)}
                        <div style={{ fontSize: 9, fontWeight: 600 }}>{di.label}</div>
                      </span>
                    </div>
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr",
                        gap: 8,
                      }}
                    >
                      <div
                        style={{
                          background: "#0f172a",
                          borderRadius: 8,
                          padding: "8px",
                          textAlign: "center",
                        }}
                      >
                        <div
                          style={{
                            color: "#a78bfa",
                            fontSize: 16,
                            fontWeight: 700,
                          }}
                        >
                          {e.camera_replies}
                        </div>
                        <div
                          style={{ color: "#475569", fontSize: 10, marginTop: 2 }}
                        >
                          كاميرات
                        </div>
                      </div>
                      <div
                        style={{
                          background: "#0f172a",
                          borderRadius: 8,
                          padding: "8px",
                          textAlign: "center",
                        }}
                      >
                        <div
                          style={{
                            color: acc !== null ? accuracyColor(acc) : "#64748b",
                            fontSize: 16,
                            fontWeight: 700,
                          }}
                        >
                          {acc !== null ? pct(acc) : "—"}
                        </div>
                        <div
                          style={{ color: "#475569", fontSize: 10, marginTop: 2 }}
                        >
                          الدقة
                        </div>
                      </div>
                    </div>
                    {e.notes && (
                      <div
                        style={{
                          marginTop: 8,
                          color: "#64748b",
                          fontSize: 11,
                        }}
                      >
                        📝 {e.notes}
                      </div>
                    )}
                  </Card>
                );
              })
            )}
          </div>
        )}

        {/* STATIONS */}
        {tab === "stations" && (
          <div>
            <h3
              style={{
                color: "#f1f5f9",
                margin: "0 0 14px",
                fontSize: 15,
                fontWeight: 700,
              }}
            >
              محطاتي ({stations.length})
            </h3>
            {stations.map((s) => (
              <Card
                key={s.id}
                style={{ padding: "14px 18px", marginBottom: 10 }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <div>
                    <div
                      style={{
                        color: "#f1f5f9",
                        fontWeight: 700,
                        fontSize: 14,
                      }}
                    >
                      {s.name}
                    </div>
                    <div
                      style={{
                        color: "#64748b",
                        fontSize: 12,
                        marginTop: 2,
                      }}
                    >
                      📍 {s.region}
                    </div>
                  </div>
                  <Btn
                    size="sm"
                    color="#22c55e"
                    onClick={() => {
                      setForm((f) => ({ ...f, station_id: String(s.id) }));
                      setTab("entry");
                    }}
                  >
                    + إدخال
                  </Btn>
                </div>
              </Card>
            ))}
          </div>
        )}

        {/* ATTENDANCE */}
        {tab === "attendance" && (
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            <Card>
              <h3
                style={{
                  color: "#f1f5f9",
                  margin: "0 0 16px",
                  fontSize: 15,
                  fontWeight: 700,
                }}
              >
                🕐 دوام مشرف المحطة
              </h3>
              <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                <Input
                  label="التاريخ"
                  type="date"
                  value={attForm.date}
                  onChange={(v) => setAttForm((f) => ({ ...f, date: v }))}
                />
                <Select
                  label="المحطة"
                  value={attForm.station_id}
                  onChange={(v) => setAttForm((f) => ({ ...f, station_id: v }))}
                  options={stations.map((s) => ({
                    value: s.id,
                    label: s.name,
                  }))}
                  required
                />
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: 12,
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 6,
                    }}
                  >
                    <label style={{ color: "#94a3b8", fontSize: 13 }}>
                      وقت الحضور
                    </label>
                    <input
                      type="time"
                      value={attForm.check_in}
                      onChange={(e) =>
                        setAttForm((f) => ({ ...f, check_in: e.target.value }))
                      }
                      style={{
                        background: "#0f172a",
                        border: "1px solid #334155",
                        borderRadius: 10,
                        padding: "10px 13px",
                        color: "#f1f5f9",
                        fontSize: 14,
                        fontFamily: "inherit",
                        outline: "none",
                        width: "100%",
                        boxSizing: "border-box",
                      }}
                    />
                  </div>
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 6,
                    }}
                  >
                    <label style={{ color: "#94a3b8", fontSize: 13 }}>
                      وقت الانصراف
                    </label>
                    <input
                      type="time"
                      value={attForm.check_out}
                      onChange={(e) =>
                        setAttForm((f) => ({
                          ...f,
                          check_out: e.target.value,
                        }))
                      }
                      style={{
                        background: "#0f172a",
                        border: "1px solid #334155",
                        borderRadius: 10,
                        padding: "10px 13px",
                        color: "#f1f5f9",
                        fontSize: 14,
                        fontFamily: "inherit",
                        outline: "none",
                        width: "100%",
                        boxSizing: "border-box",
                      }}
                    />
                  </div>
                </div>
                {attForm.check_in && attForm.check_out &&
                  (() => {
                    const [h1, m1] = attForm.check_in.split(":").map(Number);
                    const [h2, m2] = attForm.check_out.split(":").map(Number);
                    let mins = h2 * 60 + m2 - (h1 * 60 + m1);
                    if (mins <= 0) mins += 24 * 60; // shift spans midnight
                    if (mins <= 0) return null;
                    return (
                      <div
                        style={{
                          background: "#0f172a",
                          borderRadius: 8,
                          padding: "8px 12px",
                          border: "1px solid #38bdf833",
                          display: "flex",
                          justifyContent: "space-between",
                        }}
                      >
                        <span style={{ color: "#94a3b8", fontSize: 13 }}>
                          المدة:
                        </span>
                        <span style={{ color: "#38bdf8", fontWeight: 800 }}>
                          {Math.floor(mins / 60)}س {mins % 60}د
                        </span>
                      </div>
                    );
                  })()}
                <Input
                  label="ملاحظات"
                  value={attForm.notes}
                  onChange={(v) => setAttForm((f) => ({ ...f, notes: v }))}
                  placeholder="اختياري..."
                />
                <button
                  onClick={submitAtt}
                  style={{
                    background: "linear-gradient(135deg,#22c55e,#16a34a)",
                    color: "#fff",
                    border: "none",
                    borderRadius: 10,
                    padding: 16,
                    fontSize: 16,
                    fontFamily: "inherit",
                    fontWeight: 700,
                    cursor: "pointer",
                  }}
                >
                  💾 حفظ الدوام
                </button>
              </div>
            </Card>

            <Card style={{ marginBottom: 80 }}>
              <h3
                style={{
                  color: "#f1f5f9",
                  margin: "0 0 14px",
                  fontSize: 14,
                  fontWeight: 700,
                }}
              >
                سجلات {formatDate(attForm.date)}
              </h3>
              {attRecords.length === 0 ? (
                <EmptyState message="لا توجد سجلات" />
              ) : (
                attRecords.map((r, i) => (
                  <div
                    key={i}
                    style={{
                      background: "#0f172a",
                      borderRadius: 10,
                      padding: "12px",
                      marginBottom: 8,
                      border: "1px solid #1e293b",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        marginBottom: 8,
                      }}
                    >
                      <span
                        style={{
                          color: "#f1f5f9",
                          fontWeight: 700,
                          fontSize: 13,
                        }}
                      >
                        {r.station_name}
                      </span>
                      <span style={{ color: "#64748b", fontSize: 11 }}>
                        {r.region}
                      </span>
                    </div>
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr",
                        gap: 8,
                      }}
                    >
                      <div
                        style={{
                          background: "#052e16",
                          borderRadius: 8,
                          padding: "8px",
                          textAlign: "center",
                        }}
                      >
                        <div
                          style={{
                            color: "#22c55e",
                            fontWeight: 800,
                            fontSize: 16,
                          }}
                        >
                          {r.check_in || "—"}
                        </div>
                        <div style={{ color: "#475569", fontSize: 10 }}>
                          حضور
                        </div>
                      </div>
                      <div
                        style={{
                          background: "#1c0303",
                          borderRadius: 8,
                          padding: "8px",
                          textAlign: "center",
                        }}
                      >
                        <div
                          style={{
                            color: "#ef4444",
                            fontWeight: 800,
                            fontSize: 16,
                          }}
                        >
                          {r.check_out || "—"}
                        </div>
                        <div style={{ color: "#475569", fontSize: 10 }}>
                          انصراف
                        </div>
                      </div>
                    </div>
                    {r.notes && (
                      <div
                        style={{
                          color: "#64748b",
                          fontSize: 11,
                          marginTop: 6,
                        }}
                      >
                        📝 {r.notes}
                      </div>
                    )}
                  </div>
                ))
              )}
            </Card>
          </div>
        )}
      </div>

      {/* Bottom Nav */}
      <div
        style={{
          position: "fixed",
          bottom: 0,
          left: "50%",
          transform: "translateX(-50%)",
          width: "100%",
          maxWidth: 480,
          background: "#0f172a",
          borderTop: "1px solid #1e3a5f",
          display: "flex",
          zIndex: 100,
        }}
      >
        {[
          ["entry", "📝", "إدخال"],
          ["history", "📊", "سجلاتي"],
          ["stations", "🏭", "محطاتي"],
          ["attendance", "🕐", "الدوام"],
        ].map(([id, icon, lbl]) => (
          <button
            key={id}
            onClick={() => setTab(id)}
            style={{
              flex: 1,
              background: "transparent",
              border: "none",
              padding: "12px 4px 16px",
              color: tab === id ? "#38bdf8" : "#475569",
              fontFamily: "inherit",
              fontWeight: tab === id ? 700 : 400,
              cursor: "pointer",
              fontSize: 10,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 3,
            }}
          >
            <span style={{ fontSize: 20 }}>{icon}</span>
            <span>{lbl}</span>
          </button>
        ))}
      </div>
    </div>
  );
}
